/**
 * The manufacturer's parts on a material order, and the warranty they add up
 * to (ADR 0309).
 *
 * The tier a roof can carry is decided by the accessories on the order. TAMKO
 * has four qualifying parts; all four is MasterShield, three is ProShield.
 * GAF has five; four is Silver Pledge, three is System Plus, and five is
 * Golden Pledge, which only a Master Elite contractor may file. This module
 * reads an order's lines and says which parts are there, which are missing,
 * and which generic line a manufacturer's part would replace. The order
 * screen prices that swap and offers it; the Warranties page lists the jobs
 * where it is worth making.
 */

import { cents, toCents, type Cents } from "./money.js";
import { MATERIAL_ORDER_BASE_STATUS } from "./orderStatus.js";
import {
  GAF_PROGRAM,
  programYear,
  REGISTERED_TIER,
  registeredTierOfEstimate,
  estimateWarrantyOf,
  tiersFor,
  type RegisteredTier,
  type WarrantyManufacturer,
} from "./warranties.js";

/* ------------------------------------------------------------------------ */
/* Accessories                                                               */
/* ------------------------------------------------------------------------ */

/** The part of the roof a line is, as far as a warranty cares. */
export type RoofPart =
  | "shingle"
  | "ice_water"
  | "underlayment"
  | "starter"
  | "hip_ridge"
  | "ventilation";

export interface AccessoryCategory {
  key: string;
  /** What the manufacturer calls it, for the screen. */
  name: string;
  part: RoofPart;
}

export const TAMKO_ACCESSORIES: readonly AccessoryCategory[] = [
  { key: "moisture_guard", name: "TAMKO Moisture Guard", part: "ice_water" },
  {
    key: "synthetic_guard",
    name: "TAMKO Synthetic Guard underlayment",
    part: "underlayment",
  },
  { key: "starter", name: "TAMKO starter", part: "starter" },
  { key: "hip_ridge", name: "TAMKO hip & ridge", part: "hip_ridge" },
];

export const GAF_ACCESSORIES: readonly AccessoryCategory[] = [
  { key: "starter", name: "GAF starter strip", part: "starter" },
  {
    key: "deck_protection",
    name: "GAF roof deck protection",
    part: "underlayment",
  },
  { key: "leak_barrier", name: "GAF leak barrier", part: "ice_water" },
  { key: "ventilation", name: "GAF attic ventilation", part: "ventilation" },
  { key: "ridge_cap", name: "GAF ridge cap shingles", part: "hip_ridge" },
];

export function accessoriesOf(
  manufacturer: WarrantyManufacturer,
): readonly AccessoryCategory[] {
  return manufacturer === "TAMKO" ? TAMKO_ACCESSORIES : GAF_ACCESSORIES;
}

export function accessoryName(
  manufacturer: WarrantyManufacturer | null | undefined,
  key: string,
): string {
  if (!manufacturer) return key;
  return accessoriesOf(manufacturer).find((a) => a.key === key)?.name ?? key;
}

const ICE_WATER =
  /ice\s*(&|and)?\s*water|moisture\s*guard|storm\s*guard|weather\s*watch|leak\s*barrier/i;
const UNDERLAYMENT =
  /underlayment|felt\s*buster|synthetic|deck.?armor|tiger\s*paw|\bfelt\b/i;
const STARTER = /starter|pro.?start/i;
const VENTILATION = /\bvents?\b|ventilation|turbine|attic|\bcfm\b/i;
const HIP_RIDGE =
  /ridge\s*cap|hip\s*(&|and)?\s*ridge|\bh\s*&\s*r\b|seal.?a.?ridge|timbertex|proline/i;
const SHINGLE = /titan|timberline|heritage|\bhdz\b|uhdz|shingle/i;

/**
 * Which part of the roof a line is, from its name and its catalogue category.
 *
 * The name is read first because the catalogue is not to be trusted here: the
 * generic "Ice & Water Shield" is filed under Underlayment, and ridge-vent
 * labour under Hip & Ridge. A vent is never a ridge cap, whatever its row
 * says, so ventilation is tested before hip & ridge — "Cobra Ridge Vent"
 * carries both words.
 */
export function roofPart(
  name: string | null | undefined,
  category?: string | null,
): RoofPart | null {
  const n = (name ?? "").trim();
  if (ICE_WATER.test(n)) return "ice_water";
  if (UNDERLAYMENT.test(n)) return "underlayment";
  if (STARTER.test(n)) return "starter";
  if (VENTILATION.test(n)) return "ventilation";
  if (HIP_RIDGE.test(n)) return "hip_ridge";
  const c = (category ?? "").trim().toLowerCase();
  if (c === "ice & water shield") return "ice_water";
  if (c === "underlayment") return "underlayment";
  if (c === "starter") return "starter";
  if (c === "ventilation") return "ventilation";
  if (c === "hip & ridge") return "hip_ridge";
  if (c === "shingles" || SHINGLE.test(n)) return "shingle";
  return null;
}

const TAMKO_NAME =
  /tamko|titan\s*xt|moisture\s*guard|synthetic\s*guard|proline|heritage/i;
const GAF_NAME =
  /\bgaf\b|timberline|pro.?start|felt\s*buster|storm\s*guard|weather\s*watch|seal.?a.?ridge|timbertex|cobra|master\s*flow|deck.?armor|tiger\s*paw|\bhdz\b|uhdz/i;

/** Whose product a line is, by name. Null for a generic or unknown part. */
export function lineManufacturer(
  name: string | null | undefined,
): WarrantyManufacturer | null {
  const n = name ?? "";
  if (TAMKO_NAME.test(n)) return "TAMKO";
  if (GAF_NAME.test(n)) return "GAF";
  return null;
}

/** A material order line, as much of it as a warranty reads. */
export interface WarrantyLine {
  id?: string | undefined;
  product_id?: string | null | undefined;
  name: string | null;
  category?: string | null | undefined;
  qty?: number | string | null | undefined;
  unit?: string | null | undefined;
  unit_price?: number | string | null | undefined;
}

/** Whose roof an order is, from its shingle line. Null when it has none. */
export function orderManufacturer(
  lines: readonly WarrantyLine[],
): WarrantyManufacturer | null {
  for (const line of lines) {
    if (roofPart(line.name, line.category) !== "shingle") continue;
    const who = lineManufacturer(line.name);
    if (who) return who;
  }
  return null;
}

export interface MissingAccessory extends AccessoryCategory {
  /** The generic line doing that job today, which the part would replace. */
  replaces: WarrantyLine | null;
}

export interface OrderAccessories {
  manufacturer: WarrantyManufacturer;
  present: AccessoryCategory[];
  missing: MissingAccessory[];
}

/**
 * The manufacturer's qualifying parts on an order, and the ones it lacks.
 *
 * A part counts when a line does that job and the line is the manufacturer's
 * own. A line doing the job in somebody else's name is the one the
 * manufacturer's part would replace, which is what the swap is priced on.
 */
export function orderAccessories(
  manufacturer: WarrantyManufacturer,
  lines: readonly WarrantyLine[],
): OrderAccessories {
  const present: AccessoryCategory[] = [];
  const missing: MissingAccessory[] = [];
  for (const category of accessoriesOf(manufacturer)) {
    let own: WarrantyLine | null = null;
    let generic: WarrantyLine | null = null;
    for (const line of lines) {
      if (roofPart(line.name, line.category) !== category.part) continue;
      if (lineManufacturer(line.name) === manufacturer) own = line;
      else if (generic === null) generic = line;
    }
    if (own) present.push(category);
    else missing.push({ ...category, replaces: generic });
  }
  return { manufacturer, present, missing };
}

/** The tier a roof with this many of the manufacturer's parts may file. */
export function tierForAccessories(
  manufacturer: WarrantyManufacturer,
  count: number,
): RegisteredTier | null {
  const tiers = tiersFor(manufacturer).sort(
    (a, b) => REGISTERED_TIER[b].accessories - REGISTERED_TIER[a].accessories,
  );
  return (
    tiers.find((tier) => count >= REGISTERED_TIER[tier].accessories) ?? null
  );
}

/**
 * The tier worth aiming an order at.
 *
 * TAMKO's best is MasterShield, and the programme wants ten a year. GAF's
 * Golden Pledge is only filed by a Master Elite contractor, which the company
 * is not yet, so Silver Pledge is the one to reach.
 */
export function targetTier(manufacturer: WarrantyManufacturer): RegisteredTier {
  return manufacturer === "TAMKO" ? "tamko_mastershield" : "gaf_silver_pledge";
}

export interface OrderWarrantyAdvice extends OrderAccessories {
  /** What the order qualifies for as it stands. */
  tier: RegisteredTier | null;
  target: RegisteredTier;
  /** Parts short of the target tier. Zero means it is there. */
  short: number;
  /** The parts that would reach the target, in the order they are listed. */
  toTarget: MissingAccessory[];
}

/**
 * What an order's lines say about the warranty, or null for a roof that is
 * neither TAMKO nor GAF.
 */
export function orderWarrantyAdvice(
  lines: readonly WarrantyLine[],
): OrderWarrantyAdvice | null {
  const manufacturer = orderManufacturer(lines);
  if (!manufacturer) return null;
  const found = orderAccessories(manufacturer, lines);
  const target = targetTier(manufacturer);
  const short = Math.max(
    0,
    REGISTERED_TIER[target].accessories - found.present.length,
  );
  return {
    ...found,
    tier: tierForAccessories(manufacturer, found.present.length),
    target,
    short,
    toTarget: found.missing.slice(0, short),
  };
}

/**
 * The manufacturer's product that does a part's job, from the catalogue.
 *
 * The first one whose name is the manufacturer's and whose part matches. The
 * catalogue carries one of each today; if that changes this picks the one
 * listed first.
 */
export function manufacturerProductFor<
  P extends { name: string; category?: string | null; active?: boolean | null },
>(
  manufacturer: WarrantyManufacturer,
  part: RoofPart,
  products: readonly P[],
): P | null {
  for (const product of products) {
    if (product.active === false) continue;
    if (lineManufacturer(product.name) !== manufacturer) continue;
    if (roofPart(product.name, product.category) === part) return product;
  }
  return null;
}

/**
 * What swapping one line for the manufacturer's part adds to the order: the
 * line's quantity at the new cost, less what the line costs now. Negative
 * when the manufacturer's part is cheaper, which does happen.
 */
export function swapCost(line: WarrantyLine, newUnitCost: Cents): Cents {
  const qty = Number(line.qty ?? 0);
  if (!Number.isFinite(qty) || qty <= 0) return cents(0);
  const now = toCents(line.unit_price ?? 0);
  return cents(Math.round(qty * (newUnitCost - now)));
}

/**
 * An order's lines once one of them is swapped for the manufacturer's part:
 * the line keeps its quantity and takes the part's name and category.
 */
export function linesAfterSwap<L extends WarrantyLine>(
  lines: readonly L[],
  lineId: string,
  product: { id: string; name: string; category?: string | null },
): L[] {
  return lines.map((line) =>
    line.id === lineId
      ? {
          ...line,
          product_id: product.id,
          name: product.name,
          category: product.category ?? null,
        }
      : line,
  );
}

/**
 * The estimate's warranty once a swap on its order reaches a better tier,
 * or null when the estimate stays as it is.
 *
 * A Standard Architectural estimate charges ProShield's $5 a square; when a
 * swap brings its order to all four TAMKO parts it charges MasterShield's $7,
 * because that is what TAMKO will be paid. Jonathon, 2026-10-27: "7. a".
 *
 * Only upward, only within the estimate's own manufacturer, and never past
 * the tier worth aiming at: a GAF roof is charged Silver Pledge however many
 * parts it has, because Golden Pledge is not one the company can file.
 */
export function estimateWarrantyAfterSwap(
  estimateWarranty: string | null | undefined,
  orderTier: RegisteredTier | null,
): string | null {
  const charged = registeredTierOfEstimate(estimateWarranty);
  if (!charged || !orderTier) return null;
  const manufacturer = REGISTERED_TIER[charged].manufacturer;
  if (REGISTERED_TIER[orderTier].manufacturer !== manufacturer) return null;
  const target = targetTier(manufacturer);
  const reached =
    REGISTERED_TIER[orderTier].accessories > REGISTERED_TIER[target].accessories
      ? target
      : orderTier;
  if (
    REGISTERED_TIER[reached].accessories <= REGISTERED_TIER[charged].accessories
  )
    return null;
  return estimateWarrantyOf(reached);
}

/**
 * The manufacturer's registration fee for a tier on a roof: its per-square
 * rate times the squares. Null when there is no tier, the tier has no
 * per-square rate, or the squares are not a positive number.
 */
export function registrationFee(
  tier: RegisteredTier | null,
  squares: number,
): Cents | null {
  if (!tier) return null;
  return feeStep(null, tier, squares);
}

/**
 * What registering the better tier costs over the one the order has now,
 * per the manufacturer's per-square fees. Null when either fee is unknown.
 */
export function feeStep(
  from: RegisteredTier | null,
  to: RegisteredTier,
  squares: number,
): Cents | null {
  const toRate = REGISTERED_TIER[to].perSquare;
  const fromRate = from ? REGISTERED_TIER[from].perSquare : cents(0);
  if (toRate === null || fromRate === null) return null;
  if (!Number.isFinite(squares) || squares <= 0) return null;
  return cents(Math.round(squares * (toRate - fromRate)));
}

/**
 * Squares of GAF shingles on a set of order lines — the rewards estimate.
 *
 * Three bundles to a square unless the line is already in squares.
 */
export function rewardsSquaresOf(lines: readonly WarrantyLine[]): number {
  let squares = 0;
  for (const line of lines) {
    if (roofPart(line.name, line.category) !== "shingle") continue;
    if (lineManufacturer(line.name) !== "GAF") continue;
    const qty = Number(line.qty ?? 0);
    if (!Number.isFinite(qty) || qty <= 0) continue;
    squares += /sq/i.test(line.unit ?? "") ? qty : qty / 3;
  }
  return Math.round(squares * 100) / 100;
}

/* ------------------------------------------------------------------------ */
/* What the Warranties page lists                                            */
/* ------------------------------------------------------------------------ */

/**
 * The stages a job's order is still worth changing in: sold, and not yet
 * finished. A job further on has its roof on already.
 */
export const PART_SHORT_STAGES: readonly string[] = ["signed", "in_production"];

/** A material order with its lines, as the Warranties page reads it. */
export interface OrderForAdvice<J extends { stage?: string | null }> {
  id: string;
  order_number: number | null;
  status: string | null;
  job: J | null;
  /** Its lines, each carrying its catalogue category where it has one. */
  lines: readonly WarrantyLine[];
}

/** An unplaced order short of the tier it could reach. */
export interface PartShortOrder<J> {
  orderId: string;
  orderNumber: number | null;
  status: string | null;
  job: J;
  advice: OrderWarrantyAdvice;
}

/**
 * The orders a part short of the best warranty their roof could carry.
 *
 * Only unplaced ones, on jobs still in hand: that is the only status the
 * order screen offers the swap on, and once placed the parts are bought.
 */
export function partShortOrders<J extends { stage?: string | null }>(
  orders: readonly OrderForAdvice<J>[],
): PartShortOrder<J>[] {
  const short: PartShortOrder<J>[] = [];
  for (const order of orders) {
    if (order.status !== MATERIAL_ORDER_BASE_STATUS) continue;
    if (!order.job) continue;
    if (!PART_SHORT_STAGES.includes(order.job.stage ?? "")) continue;
    const advice = orderWarrantyAdvice(order.lines);
    if (!advice || advice.short === 0) continue;
    short.push({
      orderId: order.id,
      orderNumber: order.order_number,
      status: order.status,
      job: order.job,
      advice,
    });
  }
  return short;
}

/**
 * The GAF rewards-squares estimate for the GAF year `today` falls in: the GAF
 * shingle on the orders of jobs whose work was completed inside that year.
 *
 * `completions` are All Work Complete ticks; `orders` the live orders of
 * those jobs. A completion outside the year, or an order on a job not
 * completed in it, counts for nothing.
 */
export function gafRewardsSquares(
  completions: readonly { job_id: string; completed_at: string | null }[],
  orders: readonly { job_id: string; lines: readonly WarrantyLine[] }[],
  today: string,
): number {
  const year = programYear(GAF_PROGRAM.start, today);
  const jobs = new Set(
    completions
      .filter((c) => {
        const day = c.completed_at?.slice(0, 10);
        return day !== undefined && day >= year.from && day < year.to;
      })
      .map((c) => c.job_id),
  );
  return rewardsSquaresOf(
    orders.filter((o) => jobs.has(o.job_id)).flatMap((o) => o.lines),
  );
}
